import { createContext, useContext, useEffect, useMemo } from "react";
import { RainbowKitProvider, darkTheme } from "@rainbow-me/rainbowkit";
import "@rainbow-me/rainbowkit/styles.css";
import { WagmiConfig, useConnect } from "wagmi";
import { Disclaimer } from "~/client/components/molecules";
import { useSessionDefaultChainId } from "~/client/hooks";
import type { IContext } from "./logic";
import type { PropsWithChildren } from "react";
import { configure } from "./connectors";
import useConfig, { initial, isHostCypress, isHostSafe } from "./logic";

export { isHostSafe, isHostCypress };

const Context = createContext<IContext>(initial);

export function useWeb3Context(): IContext {
  return useContext(Context);
}

/**
 * ------------------------------
 * Context Providers
 * ------------------------------
 */

function Web3StateProvider({ children }: PropsWithChildren<unknown>) {
  const value: IContext = useConfig();

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

function AutoConnectProvider({ children }: PropsWithChildren<unknown>) {
  const { connect, connectors } = useConnect();

  useEffect(() => {
    if (isHostSafe) {
      /**
       * This assumes the "Safe Connector" will be ready to connect before
       * any other due to its position in the connector queue. As
       * autoConnect is disabled, we simply attempt to connect with
       * the safe wallet.
       */

      for (const connector of connectors) {
        if (connector.ready) {
          connect({ connector });
          break;
        }
      }
    }
  }, [connect, connectors]);

  return <>{children}</>;
}

export default function Web3Provider({ children }: PropsWithChildren<unknown>) {
  const { chainId: defaultChainId } = useSessionDefaultChainId();

  const { chains, config } = useMemo(
    () =>
      configure({
        first: defaultChainId,
        isHostSafe,
        isHostCypress,
      }),
    [defaultChainId],
  );

  return (
    <WagmiConfig config={config}>
      <RainbowKitProvider
        appInfo={{
          appName: "Sablier V2",
          disclaimer: Disclaimer,
        }}
        chains={chains}
        initialChain={defaultChainId}
        theme={darkTheme()}
      >
        <Web3StateProvider>
          <AutoConnectProvider>{children}</AutoConnectProvider>
        </Web3StateProvider>
      </RainbowKitProvider>
    </WagmiConfig>
  );
}
